type Listener<T0, T1, T2, T3, T4, T5> =
    (param0: T0, param1: T1, param2: T2, param3: T3, param4: T4, param5: T5) => void;

class ListenerNode<T0, T1, T2, T3, T4, T5> {
    public nextNode: ListenerNode<T0, T1, T2, T3, T4, T5>;
    public prevNode: ListenerNode<T0, T1, T2, T3, T4, T5>;

    public constructor(
        private readonly func: Listener<T0, T1, T2, T3, T4, T5>,
        private readonly context: Object,
        public readonly once: boolean = false) {
    }

    public call(param0: T0, param1: T1, param2: T2, param3: T3, param4: T4, param5: T5): void {
        this.func.apply(this.context, arguments);
    }

    public equal(func: Listener<T0, T1, T2, T3, T4, T5>, context: Object): boolean {
        return this.func === func && this.context === context;
    }
}

export class Event<T0 = void, T1 = void, T2 = void, T3 = void, T4 = void, T5 = void> {
    private firstNode: ListenerNode<T0, T1, T2, T3, T4, T5>;
    private lastNode: ListenerNode<T0, T1, T2, T3, T4, T5>;

    public add(listener: Listener<T0, T1, T2, T3, T4, T5>, context?: Object): void {
        this.addNode(new ListenerNode(listener, context, false));
    }

    public once(listener: Listener<T0, T1, T2, T3, T4, T5>, context?: Object): void {
        this.addNode(new ListenerNode(listener, context, true));
    }

    public remove(listener: Listener<T0, T1, T2, T3, T4, T5>, context?: Object): void {
        let node = this.firstNode;
        while (node != null) {
            if (node.equal(listener, context)) {
                this.removeNode(node);
                return;
            }
            node = node.nextNode;
        }
    }

    public removeAll(): void {
        let node = this.firstNode;
        while (node != null) {
            this.removeNode(node);
            node = node.nextNode;
        }
    }

    public emit(param0: T0, param1: T1, param2: T2, param3: T3, param4: T4, param5: T5): void {
        let node = this.firstNode;
        while (node != null) {
            if (node.once) {
                this.removeNode(node);
            }
            node.call(param0, param1, param2, param3, param4, param5);
            node = node.nextNode;
        }
    }

    private addNode(node: ListenerNode<T0, T1, T2, T3, T4, T5>): void {
        if (this.firstNode == null) {
            this.firstNode = this.lastNode = node;
            return;
        }
        this.lastNode.nextNode = node;
        node.prevNode = this.lastNode;
        this.lastNode = node;
    }

    private removeNode(node: ListenerNode<T0, T1, T2, T3, T4, T5>) {
        if (node === this.firstNode) {
            this.firstNode = node.nextNode;
        }
        if (node === this.lastNode) {
            this.lastNode = node.prevNode;
        }
        if (node.prevNode != null) {
            node.prevNode.nextNode = node.nextNode;
        }
        if (node.nextNode != null) {
            node.nextNode.prevNode = node.prevNode;
        }
    }
}